/**
 * Epic AB#1824 — Single chip cell inside the matrix.
 *
 * The cell has multiple visual states:
 *   - `closed`         — grey chip, "Closed" text
 *   - `open`           — neutral chip, "HH:mm – HH:mm"
 *   - `matches-default`— greyed italic "(matches default)" instead of chip
 *   - `divergent`      — accent-bordered chip when value differs from Default
 *   - `unsaved`        — warning-bordered chip while local edit is dirty
 *   - `stale`          — danger-bordered chip after 409 Hours.Stale
 *   - `editing`        — replaced by `<CellEditor>` inline (overflow visible)
 *
 * The cell is a button so keyboard activation works without extra handlers.
 * Arrow-key navigation between cells is handled by the parent matrix —
 * the cell exposes `onArrowKey` for that.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from './interpolate';
import { formatCellTime, validateCellValue, type CellState, type CellValue } from './matrixTypes';
import styles from './BusinessHoursMatrix.module.css';

export interface MatrixDayCellProps {
    cell: CellState;
    branchName: string;
    /**
     * The Default-row "majority" value for this day. When non-null AND the
     * cell value equals it, the cell shows the "(matches default)" sentinel
     * instead of an editable chip.
     */
    defaultValue: CellValue | null;
    /**
     * True when this row is the synthetic Default row. Default row cells
     * never render the "matches default" sentinel — they ARE the default.
     */
    isDefaultRow: boolean;
    /** Branch row only: true when at least one cell on this row is divergent. */
    isDivergentChip: boolean;
    /**
     * Optional badge count for Default-row cells (e.g. "1 differs"). Computed
     * by the matrix on the divergent-mode path.
     */
    differsCount: number;
    /**
     * When true, render the inline editor instead of the chip. Parent owns
     * the open/close — clicking the chip dispatches `onRequestEdit`.
     */
    isEditing: boolean;
    onRequestEdit: () => void;
    onSubmit: (next: CellValue) => void;
    onCancelEdit: () => void;
    /** Only relevant for branch-row divergent cells with revert tip. */
    canRevert: boolean;
    onRequestRevert: () => void;
    /** Keyboard navigation between cells inside the matrix grid. */
    onArrowKey?: (direction: 'left' | 'right' | 'up' | 'down' | 'home' | 'end') => void;
    /**
     * AB#1824 polish — callback ref the parent matrix uses to register
     * each cell's button into a `(rowKey, dow) → HTMLButtonElement` map
     * so it can `.focus()` the next cell when `onArrowKey` fires. Without
     * this, arrow-key nav was declared on the cell but never wired — Tab
     * worked, arrows were no-ops (Omar N1, fast-follow).
     */
    cellButtonRef?: (el: HTMLButtonElement | null) => void;
    /** Last cell of the last row — drops the bottom border. */
    isLastRow: boolean;
    /** A11y label override — built by the matrix from i18n templates. */
    ariaLabel: string;
}

/** Round any time string to the nearest 15-minute step. */
function step15(time: string): string {
    if (!/^\d{2}:\d{2}$/.test(time)) return time;
    const [h, m] = time.split(':').map(Number);
    const rounded = Math.round(m / 15) * 15;
    if (rounded === 60) {
        return `${String(h + 1).padStart(2, '0')}:00`;
    }
    return `${String(h).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
}

const MatrixDayCell: React.FC<MatrixDayCellProps> = ({
    cell,
    branchName,
    defaultValue,
    isDefaultRow,
    isDivergentChip,
    differsCount,
    isEditing,
    onRequestEdit,
    onSubmit,
    onCancelEdit,
    canRevert,
    onRequestRevert,
    onArrowKey,
    cellButtonRef,
    isLastRow,
    ariaLabel,
}) => {
    const { t } = useLanguage();
    const cellRef = useRef<HTMLButtonElement>(null);

    // Editor-local draft. Times are held as raw hour/minute digit strings so a
    // two-digit value (e.g. 18) can be typed digit-by-digit. The old code bound
    // each field to a slice of a padded "HH:mm" string, which — with the 2-char
    // maxLength — left the field permanently full and dropped the next keystroke
    // (AB#2155: the close time could not be changed).
    const [isOpen, setIsOpen] = useState<boolean>(cell.isOpen);
    const [is24Hours, setIs24Hours] = useState<boolean>(cell.is24Hours);
    const [openHH, setOpenHH] = useState<string>('09');
    const [openMM, setOpenMM] = useState<string>('00');
    const [closeHH, setCloseHH] = useState<string>('17');
    const [closeMM, setCloseMM] = useState<string>('00');
    const [editorError, setEditorError] = useState<string | null>(null);

    // Re-seed the editor every time it opens.
    useEffect(() => {
        if (!isEditing) return;
        const [oh, om] = (cell.openTime ?? '09:00').split(':');
        const [ch, cm] = (cell.closeTime ?? '17:00').split(':');
        setIsOpen(cell.isOpen);
        setIs24Hours(cell.is24Hours);
        setOpenHH(oh);
        setOpenMM(om);
        setCloseHH(ch);
        setCloseMM(cm);
        setEditorError(null);
    }, [isEditing, cell]);

    // Auto-focus + select the first editor input when editing opens.
    const openHourRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (isEditing && openHourRef.current) {
            openHourRef.current.focus();
            openHourRef.current.select();
        }
    }, [isEditing]);

    const handleSubmit = useCallback(() => {
        const pad = (s: string) => (s === '' ? '00' : s.padStart(2, '0'));
        const normalized: CellValue = {
            isOpen,
            is24Hours,
            openTime: isOpen ? step15(`${pad(openHH)}:${pad(openMM)}`) : null,
            closeTime: isOpen ? step15(`${pad(closeHH)}:${pad(closeMM)}`) : null,
        };
        const v = validateCellValue(normalized);
        if (!v.ok) {
            setEditorError(v.error ?? null);
            return;
        }
        setEditorError(null);
        onSubmit(normalized);
    }, [isOpen, is24Hours, openHH, openMM, closeHH, closeMM, onSubmit]);

    const handleKey = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRequestEdit();
                return;
            }
            if (!onArrowKey) return;
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    onArrowKey('left');
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    onArrowKey('right');
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    onArrowKey('up');
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    onArrowKey('down');
                    break;
                case 'Home':
                    e.preventDefault();
                    onArrowKey('home');
                    break;
                case 'End':
                    e.preventDefault();
                    onArrowKey('end');
                    break;
                default:
                    break;
            }
        },
        [onArrowKey, onRequestEdit],
    );

    const cellClass = `${styles.dayCell} ${isEditing ? styles.dayCellEditing : ''} ${isLastRow ? styles.dayCellLastRow : ''}`;

    if (isEditing) {
        // Inline editor — rendered as a div with overflow visible. The parent
        // cell button wrapper is suppressed in this branch.
        return (
            <div className={cellClass} aria-label={ariaLabel} role="gridcell">
                <div className={styles.cellEditor} role="dialog" aria-label={ariaLabel}>
                    <div className={styles.cellEditorPickers}>
                        <input
                            ref={openHourRef}
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            value={openHH}
                            onChange={(e) => setOpenHH(e.target.value.replace(/\D/g, '').slice(0, 2))}
                            onFocus={(e) => e.currentTarget.select()}
                            aria-label={t('hoursMatrix.editor.openHour')}
                            disabled={!isOpen}
                        />
                        <span className={styles.cellEditorColon}>:</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            value={openMM}
                            onChange={(e) => setOpenMM(e.target.value.replace(/\D/g, '').slice(0, 2))}
                            onFocus={(e) => e.currentTarget.select()}
                            aria-label={t('hoursMatrix.editor.openMinute')}
                            disabled={!isOpen}
                        />
                        <span className={styles.cellEditorDash}>–</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            value={closeHH}
                            onChange={(e) => setCloseHH(e.target.value.replace(/\D/g, '').slice(0, 2))}
                            onFocus={(e) => e.currentTarget.select()}
                            aria-label={t('hoursMatrix.editor.closeHour')}
                            disabled={!isOpen}
                        />
                        <span className={styles.cellEditorColon}>:</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            value={closeMM}
                            onChange={(e) => setCloseMM(e.target.value.replace(/\D/g, '').slice(0, 2))}
                            onFocus={(e) => e.currentTarget.select()}
                            aria-label={t('hoursMatrix.editor.closeMinute')}
                            disabled={!isOpen}
                        />
                    </div>
                    <div className={styles.cellEditorClosedRow}>
                        <button
                            type="button"
                            className={`${styles.toggleBtn} ${!isOpen ? styles.toggleBtnOn : ''}`}
                            onClick={() => setIsOpen((v) => !v)}
                            aria-pressed={!isOpen}
                            aria-label={t('hoursMatrix.editor.closedAllDay')}
                        />
                        <span>{t('hoursMatrix.editor.closedAllDay')}</span>
                    </div>
                    <div className={styles.cellEditorActions}>
                        <button
                            type="button"
                            className={styles.cellEditorSave}
                            onClick={handleSubmit}
                        >
                            {t('hoursMatrix.editor.save')}
                        </button>
                        <button
                            type="button"
                            className={styles.cellEditorCancel}
                            onClick={onCancelEdit}
                        >
                            {t('hoursMatrix.editor.cancel')}
                        </button>
                    </div>
                    {editorError && (
                        <div className={styles.cellEditorError} role="alert">
                            {editorError}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const formatted = formatCellTime(cell);
    const matchesDefault =
        !isDefaultRow
        && defaultValue !== null
        && cell.isOpen === defaultValue.isOpen
        && cell.is24Hours === defaultValue.is24Hours
        && (cell.isOpen
            ? cell.openTime === defaultValue.openTime && cell.closeTime === defaultValue.closeTime
            : true);

    let chip: React.ReactNode = null;
    if (cell.isStale) {
        chip = (
            <span className={`${styles.cellChip} ${styles.cellChipStale}`}>
                <span className={styles.cellChipGlyph}>⚠</span>
                {formatted.kind === 'open' ? formatted.text : t('hoursMatrix.cell.closed')}
            </span>
        );
    } else if (cell.isDirty) {
        chip = (
            <span className={`${styles.cellChip} ${styles.cellChipUnsaved}`}>
                {formatted.kind === 'open' ? formatted.text : t('hoursMatrix.cell.closed')}
            </span>
        );
    } else if (!isDefaultRow && matchesDefault) {
        chip = (
            <span className={styles.cellMatches}>
                {t('hoursMatrix.cell.matchesDefault')}
            </span>
        );
    } else if (!isDefaultRow && isDivergentChip) {
        chip = (
            <span className={`${styles.cellChip} ${styles.cellChipDivergent}`}>
                {formatted.kind === 'open' ? formatted.text : t('hoursMatrix.cell.closed')}
            </span>
        );
    } else if (formatted.kind === 'open') {
        chip = (
            <span className={`${styles.cellChip} ${styles.cellChipOpen}`}>
                {formatted.text}
            </span>
        );
    } else {
        chip = (
            <span className={`${styles.cellChip} ${styles.cellChipClosed}`}>
                {t('hoursMatrix.cell.closed')}
            </span>
        );
    }

    const showDiffersMeta = isDefaultRow && differsCount > 0;
    const showStaleMeta = cell.isStale && !showDiffersMeta;
    const showRevertTip = canRevert && !isDefaultRow && !matchesDefault && !cell.isStale;

    return (
        <button
            ref={(el) => {
                // AB#1824 polish — keep internal cellRef for the local
                // editor-focus effect AND register with the parent
                // matrix's ref-map so arrow-key nav can `.focus()` us.
                (cellRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
                cellButtonRef?.(el);
            }}
            type="button"
            className={cellClass}
            onClick={onRequestEdit}
            onKeyDown={handleKey}
            role="gridcell"
            aria-label={ariaLabel}
        >
            {chip}
            {showDiffersMeta && (
                <span className={styles.cellMeta} aria-hidden="true">
                    {differsCount === 1
                        ? interpolate(t('hoursMatrix.cell.differsCount.one'), { n: differsCount })
                        : interpolate(t('hoursMatrix.cell.differsCount.other'), { n: differsCount })}
                </span>
            )}
            {showStaleMeta && (
                <span className={`${styles.cellMeta} ${styles.cellMetaStale}`} aria-hidden="true">
                    {t('hoursMatrix.cell.stale')}
                </span>
            )}
            {showRevertTip && (
                <span
                    className={styles.cellRevertTip}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRequestRevert();
                    }}
                    aria-hidden="true"
                >
                    {t('hoursMatrix.branch.cellRevertTip')}
                </span>
            )}
        </button>
    );
};

export default MatrixDayCell;
